/**
 * What Configure Tunnel remembers about a drop while its request is out, and
 * about one the server refused — both KEYED BY CELL (`row:level`), both pure,
 * so the rules can be read and tested without a panel.
 *
 * A single slot for the whole window was wrong as soon as a second cell was
 * dropped in (reported 2026-10-29): the second drop took the slot, the first
 * cell fell back to the stored tunnel list — which still holds the order from
 * before its drop, since nothing is patched into it any more — and the next
 * drag in that cell was measured against an order the user had already
 * replaced, losing their first drop with no note. The same slot made one
 * cell's refusal print over another cell's rows.
 */

/** The dropped order per cell, until that cell's own request answers. */
export type PendingOrders = Readonly<Record<string, readonly number[]>>;
/** The server's refusal sentence per cell, until that cell is dragged again. */
export type Refusals = Readonly<Record<string, string>>;

/** `map` with `key` set — every other cell's entry untouched. */
export const withEntry = <T>(
  map: Readonly<Record<string, T>>,
  key: string,
  value: T
): Readonly<Record<string, T>> => ({ ...map, [key]: value });

/** `map` without `key` — again, nobody else's entry. */
export const withoutEntry = <T>(
  map: Readonly<Record<string, T>>,
  key: string
): Readonly<Record<string, T>> => {
  if (!(key in map)) return map;
  const { [key]: _dropped, ...rest } = map;
  return rest;
};

/**
 * The order to SHOW `cellKey`'s tunnels in: the one dropped there if it is
 * still outstanding and still describes that cell, else `null` for "use what
 * the server holds".
 *
 * `cellTunnelIds` is the cell as the tunnel list has it now. A pending order
 * that does not cover exactly those ids is stale — a section was added or
 * deleted under it — and the server's order is the truth.
 */
export const orderForCell = (
  pending: PendingOrders,
  cellKey: string | undefined,
  cellTunnelIds: readonly number[]
): readonly number[] | null => {
  const dropped = cellKey == null ? undefined : pending[cellKey];
  if (!dropped || dropped.length !== cellTunnelIds.length) return null;
  const present = new Set(cellTunnelIds);
  return dropped.every((id) => present.has(id)) ? dropped : null;
};
